import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MapPin, Building2, Briefcase, ArrowRight } from "lucide-react";
import PublicAssistant from "../components/PublicAssistant";
import { BrandLockup, PoweredByFundle } from "../components/Brand";

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [search, setSearch] = useState("");
  const [entity, setEntity] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/jobs`)
      .then((r) => setJobs(r.data))
      .finally(() => setLoaded(true));
  }, []);

  const entities = [...new Set(jobs.map((j) => j.entity_name))];
  const filtered = jobs.filter((j) =>
    (!entity || j.entity_name === entity) &&
    (!search || `${j.title} ${j.skills}`.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-background" data-testid="careers-page">
      <header className="relative text-white overflow-hidden" style={{ backgroundColor: "#0A251D" }}>
        <img src="https://images.unsplash.com/photo-1622675363311-3e1904dc1885?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800"
          alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(10,37,29,0.92) 25%, rgba(10,37,29,0.65) 100%)" }} />
        <div className="relative max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <BrandLockup light />
          <Link to="/login" data-testid="careers-login-link" className="text-xs uppercase tracking-widest font-bold border border-white/40 px-5 py-2.5 rounded-full hover:bg-white hover:text-[#0A251D] transition-colors duration-150">
            Employee Sign in
          </Link>
        </div>
        <div className="relative max-w-5xl mx-auto px-6 pb-16 pt-10">
          <h1 className="font-heading font-black text-4xl sm:text-6xl tracking-tighter leading-[1.02] rise-in">
            Build what&apos;s next<br />with the <span className="text-[#E05A47]">ICS Group</span>.
          </h1>
          <p className="mt-5 text-[#C9DCD0] max-w-xl leading-relaxed rise-in-1">
            {jobs.length} open roles across retail real estate, architecture & design, parking solutions and prop-tech.
            Apply in minutes — our AI assistant is here if you have questions.
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex flex-wrap gap-3 items-center">
          <input data-testid="careers-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search roles or skills…" className="border bg-white rounded-xl px-4 py-2.5 text-sm w-72 focus:outline-none focus:ring-2 focus:ring-[#0A251D]" />
          <select data-testid="careers-entity-filter" value={entity} onChange={(e) => setEntity(e.target.value)} className="border bg-white rounded-xl px-3 py-2.5 text-sm">
            <option value="">All companies</option>
            {entities.map((e) => <option key={e}>{e}</option>)}
          </select>
          <span className="text-xs text-muted-foreground">{filtered.length} role(s)</span>
        </div>

        <div className="mt-6 space-y-3">
          {filtered.map((j) => (
            <Link key={j.id} to={`/careers/${j.id}`} data-testid={`job-card-${j.id}`}
              className="kpi-card flex items-center justify-between gap-4 border bg-white rounded-xl p-5 group">
              <div className="min-w-0">
                <div className="font-heading font-bold text-lg tracking-tight group-hover:text-[#0A251D] transition-colors duration-150">{j.title}</div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><Building2 size={12} /> {j.entity_name}</span>
                  <span className="inline-flex items-center gap-1"><MapPin size={12} /> {j.location_name}</span>
                  <span className="inline-flex items-center gap-1"><Briefcase size={12} /> {j.experience_min}–{j.experience_max} yrs · {j.employment_type.replace("_", " ")}</span>
                </div>
                {j.skills && <div className="mt-2 text-xs text-[#0A251D] font-medium truncate">{j.skills}</div>}
              </div>
              <ArrowRight size={18} className="shrink-0 text-muted-foreground group-hover:text-[#0A251D] transition-colors duration-150" />
            </Link>
          ))}
          {loaded && filtered.length === 0 && (
            <div className="border bg-white rounded-xl p-8 text-center text-sm text-muted-foreground">No open roles match your search right now.</div>
          )}
        </div>
      </main>

      <footer className="border-t py-6 flex justify-center">
        <PoweredByFundle />
      </footer>
      <PublicAssistant />
    </div>
  );
}
